import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import i18next from 'i18next';
import Training from './Training';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import ListGroup from 'react-bootstrap/ListGroup';
import axios from 'axios';
import './i18n';
import LoadingSpinner from './LoadingSpinner';
import liveTrainingLogo from './assets/livetraining-logo.png';
import { setTrainingTelemetryContext, trackTrainingEvent } from './trainingTelemetry';
import { MdQrCodeScanner } from 'react-icons/md';
import QrScannerModal from './QrScannerModal';
import RegistrationStation from './RegistrationStation';
import HealthStatementCard from './components/HealthStatementCard';

// Tryb stanowiska rejestracyjnego (portiernia) — drugi kontener reaktora (Task 12 planu),
// sterowany WYLACZNIE zmienna srodowiskowa builda. `REACT_APP_MODE` jest stala od momentu
// `npm run build` i nigdy nie zmienia sie miedzy renderami TEJ SAMEJ instancji strony, wiec
// wczesny return w komponencie ponizej (przed jakimkolwiek `useState`) nie lamie Rules of Hooks.
const STATION_MODE_ENABLED = (process.env.REACT_APP_MODE || '').trim() === 'registration';

// Karta „Twoje szkolenia sa aktualne" (tryb szkoleniowy, WK 2026-10-01): 3 s i sama znika
// (Fibonacci) — wczesniej modal z przyciskiem OK, ktory stal na tablecie do czasu klikniecia.
const UP_TO_DATE_CARD_MS = 3000;

// Ochrona przed petla przeladowan (link zaproszenia loguje sie sam z parametrow adresu): flaga
// „ten link doprowadzil do szkolen aktualnych i karta sie skonczyla" w sessionStorage KARTY,
// kluczowana userId + skrot kodu. Adres zostaje nietkniety, bo Exam.js/ScenarioTraining.js po
// ostatnim szkoleniu przeladowuja strone i ma ona sie zalogowac jeszcze raz (pokazac karte).
// Tryb prywatny / zablokowany storage: zapasowa flaga w pamieci modulu.
const consumedInMemory = new Map();
// Flaga wygasa po 89 s (Fibonacci): chroni WYLACZNIE przed petla przeladowan co 3 s, a nie blokuje
// linku wielokrotnego uzytku na cala sesje (nowe szkolenie przypisane po chwili musi sie otworzyc).
const LOGIN_CONSUMED_TTL_MS = 89000;
// Karta oswiadczenia bez akcji wraca na ekran bazowy (jak na stanowisku bramki, Fibonacci).
const STATEMENT_IDLE_MS = 34000;
const hashCode = (value) => {
  let h = 5381;
  for (let i = 0; i < value.length; i += 1) h = ((h * 33) ^ value.charCodeAt(i)) >>> 0;
  return h.toString(36);
};
const loginConsumedKey = (userId, code) => `reaktor.login.consumed:${userId}:${hashCode(String(code))}`;
const isLoginConsumed = (userId, code) => {
  const key = loginConsumedKey(userId, code);
  let stamp;
  try {
    stamp = Number(window.sessionStorage.getItem(key));
  } catch (e) {
    stamp = consumedInMemory.get(key); // storage niedostepny
  }
  if (!stamp) return false;
  if (Date.now() - stamp < LOGIN_CONSUMED_TTL_MS) return true;
  consumedInMemory.delete(key);
  try { window.sessionStorage.removeItem(key); } catch (e) { /* nic do usuniecia */ }
  return false;
};
const markLoginConsumed = (userId, code) => {
  const key = loginConsumedKey(userId, code);
  const now = Date.now();
  try {
    window.sessionStorage.setItem(key, String(now));
  } catch (e) {
    consumedInMemory.set(key, now); // storage niedostepny
  }
};

const RFID_CARD_FLOW_VALUE = process.env.REACT_APP_RFID_CARD_FLOW || '0';
const RFID_CARD_FLOW_ENABLED = String(RFID_CARD_FLOW_VALUE).trim() === '1';
const RFID_SEARCH_MIN_LENGTH = 3;
const CARD_DECISION = {
  UNKNOWN: 'unknown',
  HAS_CARD: 'has-card',
  NO_CARD: 'no-card',
};

const normalizeSearchTerm = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

/**
 * Wybor trybu builda — CZYSTY komponent bez wlasnych hookow. `EmployeeLoginApp` ponizej ma
 * dziesiatki `useState`/`useEffect`/`useMemo`; wczesny `return` PRZED nimi w tej samej funkcji
 * lamie Rules of Hooks (ESLint `react-hooks/rules-of-hooks`: "Hook called conditionally") i
 * `npm run build` (devops/docker/Dockerfile) odmawia kompilacji — zmierzone, nie teoria. Dwa
 * osobne komponenty naprawiaja to raz na zawsze: kazdy woła swoje hooki bezwarunkowo, `App` samo
 * nie ma zadnego hooka, wiec ESLint nie ma czego zaraportowac.
 */
const App = () => {
  // Stanowisko portierni: inny sprzet, inny operator, zero wspolnego stanu z logowaniem
  // pracownika ponizej. Tryb WYPROWADZAMY z konfiguracji przy KAZDYM renderze (nie zapisujemy go
  // do stanu przy jakims kliknieciu) — dokladnie ten sam blad co przy MR !1093, gdzie decyzja
  // zapadala, zanim dane jeszcze dojechaly. `REACT_APP_MODE` jest stala od momentu `npm run build`
  // i nigdy nie zmienia sie miedzy renderami, wiec ten wybor nigdy nie "przeskakuje" w locie.
  if (STATION_MODE_ENABLED) {
    return <RegistrationStation />;
  }
  return <EmployeeLoginApp />;
};

const EmployeeLoginApp = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(true);
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [trainingId, setTrainingId] = useState(null);
  const [userId, setUserId] = useState(null);
  // Token sesji szkolenia z getForUser/adhoc-identify: odsylany przy zapisie wyniku (submit-answers/submit-scenario).
  const [sessionProof, setSessionProof] = useState(null);
  const [personName, setPersonName] = useState(null); // „Imie Nazwisko" z getForUser (WK)
  // Oswiadczenie zdrowotne (BRC, WK 2026-10-01): { text, userId, code } dopoki osoba nie potwierdzi
  // go DZIS. Stoi PRZED `<Training>` i PRZED karta „szkolenia aktualne".
  const loginParamsRef = useRef(null); // { userId, code } z adresu, gdy zalogowano linkiem
  const [pendingStatement, setPendingStatement] = useState(null);
  const [statementBusy, setStatementBusy] = useState(false);
  const [statementError, setStatementError] = useState(null);
  const [loginByName, setLoginByName] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [error, setError] = useState(null);
  const [returnUrl, setReturnUrl] = useState(null);

  // Ad-hoc (QR/link) entry state
  const [isPreview, setIsPreview] = useState(false);
  const [isAdhoc, setIsAdhoc] = useState(false);
  const [adhocTrainingId, setAdhocTrainingId] = useState(null);
  // Wejscie NUMEREM EWIDENCYJNYM (`?adhoc=1` bez `trainingId`, Marcin 2026-10-01, amwar162):
  // konto zalozone w panelu na sam numer, bez maila i telefonu. Po rozpoznaniu czekamy na
  // „To ja" — dopiero wtedy `getForUser` po uuid wybiera NASTEPNE wymagane szkolenie tej osoby.
  const [adhocPending, setAdhocPending] = useState(null);
  const [adhocIdType, setAdhocIdType] = useState('email'); // 'email' | 'phone' | 'emai'
  const [adhocIdValue, setAdhocIdValue] = useState('');
  const [adhocError, setAdhocError] = useState(null);
  const [adhocConfirm, setAdhocConfirm] = useState(null);
  const [matchCandidates, setMatchCandidates] = useState([]);
  const [pendingNameLogin, setPendingNameLogin] = useState(null);
  const [cardDecision, setCardDecision] = useState(
    RFID_CARD_FLOW_ENABLED ? CARD_DECISION.UNKNOWN : CARD_DECISION.NO_CARD
  );
  // Skan QR plakietki (ten sam kod co na bramie i w mailu z zaproszeniem)
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [qrIdentity, setQrIdentity] = useState(null);
  const [qrError, setQrError] = useState(null);
  // Wejscie PROSTO z plakietki: telefon zeskanowal QR, backend (`/q/{token}`) przekazal token
  // w `?badge=`. Tozsamosc jest wtedy ZNANA i formularz "firma + login" musi byc niedostepny —
  // patrz `badgeEntry` w renderze i komentarz przy identifyByBadgeCode().
  const [badgeEntry, setBadgeEntry] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const [rfidUserSearch, setRfidUserSearch] = useState('');
  const [rfidUserSearchResults, setRfidUserSearchResults] = useState([]);
  const [isRfidUserSearchLoading, setIsRfidUserSearchLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const companyListUrl = `${apiUrl}companies`;
  const normalizedRfidUserSearch = normalizeSearchTerm(rfidUserSearch);
  const canShowRfidSearchResults = normalizedRfidUserSearch.length >= RFID_SEARCH_MIN_LENGTH;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlUserId = params.get('userId');
    const loginCode = params.get('code');
    const urlReturnUrl = params.get('returnUrl');
    if (urlReturnUrl) {
      setReturnUrl(urlReturnUrl);
    }

    const urlAdhoc = params.get('adhoc');
    const urlTrainingId = params.get('trainingId');
    const urlLang = params.get('lang');
    const urlPreview = params.get('preview');
    const urlBadge = params.get('badge');

    setTrainingTelemetryContext({
      entryMode: urlPreview === '1'
        ? 'preview'
        : (urlAdhoc === '1' ? 'adhoc' : (urlUserId ? 'token' : (urlBadge ? 'badge' : 'manual'))),
      trainingId: urlTrainingId,
      userUuid: urlUserId,
      language: urlLang || getLanguageCode(),
      isPreview: urlPreview === '1',
      isAdhoc: urlAdhoc === '1',
    });
    trackTrainingEvent('entry', 'training-entry', 'Training entry', {
      hasUserId: Boolean(urlUserId),
      hasCode: Boolean(loginCode),
      hasReturnUrl: Boolean(urlReturnUrl),
      hasTrainingId: Boolean(urlTrainingId),
      hasBadge: Boolean(urlBadge),
    });

    if (urlPreview === '1' && urlTrainingId) {
      // Sandbox preview: go straight into the training, no identify, no userId.
      // Nothing is recorded — the result is evaluated via preview-answers.
      setIsPreview(true);
      if (urlLang) {
        i18next.changeLanguage(urlLang);
      }
      setTrainingId(urlTrainingId);
      setShowModal(false);
    } else if (urlUserId && loginCode && isLoginConsumed(urlUserId, loginCode)) {
      // Ten link juz doprowadzil do „szkolenia aktualne" i karta sie skonczyla — ekran bazowy
      // zamiast ponownego logowania (inaczej strona petlilaby sie co 3 s).
      loadCompanies();
    } else if (urlUserId && loginCode) {
      loginParamsRef.current = { userId: urlUserId, code: loginCode };
      setIsLoading(true);
      performLogin({ userId: urlUserId, code: loginCode, lang: getLanguageCode() })
        .catch(error => {
          setShowModal(true);
          loadCompanies();
        });
    } else if (urlAdhoc === '1' && urlTrainingId) {
      // Ad-hoc QR/link entry: language comes from the link, no company/language selection.
      setIsAdhoc(true);
      setAdhocTrainingId(urlTrainingId);
      if (urlLang) {
        i18next.changeLanguage(urlLang);
      }
      setShowModal(true);
    } else if (urlBadge) {
      // Plakietka niesie tozsamosc — rozpoznajemy ja OD RAZU, bez pytania o firme i login.
      setBadgeEntry(true);
      setShowModal(true);
      identifyByBadgeCode(urlBadge, 'url');
    } else if (urlAdhoc === '1') {
      // Link/QR z okna „Dostep do modulu szkoleniowego" w panelu: bez szkolenia i bez osoby.
      // Osoba przedstawia sie numerem ewidencyjnym, szkolenie wybiera backend. ZA plakietka:
      // adres z `badge` niesie tozsamosc i nie moze dac drogi do recznego wpisania cudzej.
      setIsAdhoc(true);
      setAdhocIdType('emai');
      if (urlLang) {
        i18next.changeLanguage(urlLang);
      }
      setShowModal(true);
    } else {
      loadCompanies();
    }
  }, []);

  // Karta „szkolenia aktualne": po 3 s powrot na `returnUrl` (jak dotad) albo na ekran bazowy.
  const upToDateCardVisible = !showModal && !trainingId && Boolean(userId) && !pendingStatement;
  useEffect(() => {
    if (!upToDateCardVisible) return undefined;
    const timer = setTimeout(() => {
      // Karta „szkolenia aktualne" sie skonczyla — dopiero TERAZ link uznajemy za wykorzystany.
      const params = loginParamsRef.current;
      if (params) markLoginConsumed(params.userId, params.code);
      returnToBase();
    }, UP_TO_DATE_CARD_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upToDateCardVisible, returnUrl]);

  /** Karta oswiadczenia ma byc pokazana, gdy backend je wymaga, a osoba nie potwierdzila go dzis. */
  const statementNeeded = (healthStatement) => Boolean(
    healthStatement && healthStatement.required && !healthStatement.confirmedToday
  );

  const handleStatementConfirm = () => {
    const pending = pendingStatement;
    if (!pending || statementBusy) return;
    setStatementBusy(true);
    setStatementError(null);
    const body = { userId: pending.userId };
    if (pending.code) body.code = pending.code;
    axios.post(`${apiUrl}health-statement`, body)
      .then((response) => {
        // Sukces TYLKO gdy backend potwierdzil zapis — samo HTTP 200 nie wystarcza.
        if (response?.data?.confirmedToday !== true) throw new Error('health statement not confirmed');
        trackTrainingEvent('health_statement_confirmed', 'health-statement', 'Health statement confirmed', {});
        setStatementBusy(false);
        setPendingStatement(null);
      })
      .catch(() => {
        // Karta zostaje: bez zapisu nie wolno puscic dalej, bo oswiadczenie byloby kosmetyczne.
        setStatementBusy(false);
        setStatementError(t('health_statement_save_failed'));
      });
  };

  /**
   * Powrot do ekranu bazowego po karcie „szkolenia aktualne" / anulowaniu oswiadczenia —
   * `returnUrl`, a bez niego reset stanu BEZ przeladowania strony (reload logowal ponownie z
   * parametrow adresu i petlil sie co 3 s). Wejscie z plakietki wraca do jej rozpoznania, tak jak
   * robilo to przeladowanie, ale wymaga juz „Czy to Ty?" od czlowieka.
   */
  const returnToBase = ({ allowReturnUrl = true } = {}) => {
    if (returnUrl && allowReturnUrl) {
      window.location.href = returnUrl;
      return;
    }
    setUserId(null);
    setSessionProof(null);
    setPersonName(null);
    setAdhocConfirm(null);
    loginParamsRef.current = null;
    setTrainingId(null);
    setMessage('');
    setError(null);
    setPendingStatement(null);
    setStatementError(null);
    setStatementBusy(false);
    setQrIdentity(null);
    setShowModal(true);
    const urlBadge = new URLSearchParams(window.location.search).get('badge');
    if (urlBadge) {
      identifyByBadgeCode(urlBadge, 'url');
    } else if (!isAdhoc) {
      loadCompanies();
    }
  };

  // Karta oswiadczenia bez akcji przez 34 s = ktos odszedl od kiosku: wracamy na ekran bazowy
  // (jak Anuluj), zeby nastepna osoba nie potwierdzila oswiadczenia cudzym kontem.
  // Tylko kiosk/tablet: link osobisty z zaproszenia (`returnUrl`) to WŁASNY telefon gościa, a „powrót" znaczy tam
  // przejście na `returnUrl` = ustawienie hasła PRZED szkoleniem. E2E WK 08.10: gość EN czytający oświadczenie
  // po 34 s lądował na „Set new password", konto aktywowało się bez szkolenia.
  const statementCardVisible = !showModal && Boolean(pendingStatement);
  useEffect(() => {
    if (!statementCardVisible || statementBusy || returnUrl) return undefined;
    const timer = setTimeout(returnToBase, STATEMENT_IDLE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementCardVisible, statementBusy, statementError, returnUrl]);

  // „Anuluj" na karcie oświadczenia: na linku osobistym NIE przechodzimy na `returnUrl` (= ustawienie hasła przed
  // szkoleniem, kontrola !48) — tylko zamykamy kartę i wracamy do ekranu bazowego; link z zaproszenia działa dalej.
  const cancelStatement = () => returnToBase({ allowReturnUrl: false });

  const loadCompanies = () => {
    setIsLoading(true);
    axios.get(`${companyListUrl}`)
      .then(response => {
        setCompanies(response.data);
        setIsLoading(false);
      })
      .catch(error => {
        console.error(t('fetch_error'), error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (company) {
      const selectedCompany = companies.find(c => c.id === company);
      if (selectedCompany && selectedCompany.users) {
        setUsers(selectedCompany.users);
      } else {
        setUsers([]);
      }
      setRfidUserSearch('');
      setRfidUserSearchResults([]);
    }
  }, [company, companies]);

  useEffect(() => {
    if (!RFID_CARD_FLOW_ENABLED || cardDecision !== CARD_DECISION.HAS_CARD || !company || !canShowRfidSearchResults) {
      setRfidUserSearchResults([]);
      setIsRfidUserSearchLoading(false);
      return;
    }

    let isCancelled = false;
    setIsRfidUserSearchLoading(true);

    axios.get(`${apiUrl}card-account-candidates`, {
      params: {
        company,
        query: rfidUserSearch.trim(),
      },
    })
      .then(response => {
        if (isCancelled) return;
        setRfidUserSearchResults(response.data?.candidates || []);
        setIsRfidUserSearchLoading(false);
      })
      .catch(error => {
        if (isCancelled) return;
        console.error(t('error_occurred'), error);
        setRfidUserSearchResults([]);
        setIsRfidUserSearchLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [apiUrl, canShowRfidSearchResults, cardDecision, company, rfidUserSearch, t]);

  useEffect(() => {
    const languages = Object.keys(i18next.options.resources);
    const options = languages.map(lang => ({
      value: lang,
      label: t(`language_${lang}`)
    }));
    setLanguageOptions(options);
    // `i18next.language` to surowy odczyt z przegladarki — na polskim systemie jest to `pl-PL`,
    // a klucze zasobow to `pl`. Porownanie wprost nigdy nie trafialo, wiec select spadal na
    // options[0] (`en`) i pokazywal angielski, mimo ze interfejs renderowal sie po polsku. Ta sama
    // wartosc szla potem do backendu jako `lang` przy logowaniu — takze po skanie kodu QR, wiec
    // polski pracownik dostawal szkolenie po angielsku.
    //
    // `resolvedLanguage` zwraca jezyk, ktorego i18next NAPRAWDE uzywa (dla `pl-PL` → `pl`, dla
    // nieobslugiwanego → `fallbackLng`), czyli zawsze klucz istniejacy w zasobach. Dopasowanie po
    // dwoch literach zostaje jako zapasowe, gdyby resolvedLanguage byl niedostepny.
    const currentLang = i18next.resolvedLanguage || i18next.language;
    const shortLang = String(currentLang || '').substring(0, 2);
    setSelectedLanguage(
      options.find(option => option.value === currentLang)
      || options.find(option => option.value === shortLang)
      || options[0]
    );
  }, [t]);

  const createFakeEmailForUser = (firstName, lastName, company) => {
    const companyInfo = companies.find(c => c.id === company);
    if (!companyInfo) {
      alert(t('contact_support'));
      return null;
    }
  
    const sanitizeString = (str) => {
      const accents = {
          'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
          'Ą': 'a', 'Ć': 'c', 'Ę': 'e', 'Ł': 'l', 'Ń': 'n', 'Ó': 'o', 'Ś': 's', 'Ź': 'z', 'Ż': 'z'
      };
      return str
          .toLowerCase()
          .replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, char => accents[char])
          .replace(/[^a-z0-9-]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-+|-+$/g, '');
    };
  
    const sanitizedFirstName = sanitizeString(firstName);
    const sanitizedLastName = sanitizeString(lastName);
    const sanitizedCompanyName = sanitizeString(companyInfo.name);
  
    const emailDomain = companyInfo.email_domain ?? `${sanitizedCompanyName}.com`;
    const email = `${sanitizedFirstName}.${sanitizedLastName}@${emailDomain}`;
  
    return email.toLowerCase();
  };

  // Wybór osoby z listy „ostatnio zalogowanych" loguje po `uuid`: backend (E2E WK 08.10) oddaje już tylko skrót
  // adresu e-mail („ew…@gmail.com"), więc wpisanie go w pole loginu nie zadziałałoby — a pełnego adresu
  // publiczna trasa podawać nie może.
  const handleUserSelect = (user) => {
    performLogin({
      userId: user.uuid,
      lang: selectedLanguage ? selectedLanguage.value.substring(0, 2) : getLanguageCode(),
    // Wybór z listy odróżnialny od linku z kodem już w zdarzeniu `identified` (kontrola !48).
    }, { trybWejscia: 'list' })
      .catch(() => {
      // `performLogin` ustawia juz `error` i gasi spinner — tu tylko przerywamy lancuch (kontrola !48). Bez tego
      // odmowa backendu (403) albo blad sieci wychodzily poza komponent jako unhandled rejection; ten sam wzorzec
      // co w `handleQrConfirm` i `performGeneratedNameLogin`.
    });
  };

  const getLanguageCode = () => {
    // Get 2-letter language code with fallback to 'en'
    return (i18next.languages && i18next.languages.length > 0) 
      ? i18next.languages[0].substring(0, 2) 
      : 'en';
  };

  // `trybWejscia` nadpisuje stempel `loginMode` w telemetrii (nie idzie do backendu) — kontrola !48.
  const performLogin = (loginData, { trybWejscia } = {}) => {
    setIsLoading(true);
    return axios.post(`${apiUrl}getForUser`, loginData)
      .then(response => {
        const { message, requiredTraining, uuid, firstName: gotFirstName, lastName: gotLastName } = response.data;
        // Brak pola `healthStatement` (backend sprzed funkcji oswiadczenia) = brak wymogu, SWIADOMIE:
        // reaktor jest wdrazany na wiele instancji o roznych wersjach backendu i fail-closed
        // zablokowalby na nich KAZDE szkolenie. Backend z funkcja zawsze wysyla to pole (kontrakt §3),
        // a wylaczona flaga to `required:false`. Sciezka ad-hoc czyta stan z getForUser (jw.).
        // Oswiadczenie wymagane, ale bez tresci = zla konfiguracja: nie pokazujemy pustej karty do
        // odhaczenia (kosmetyczny zapis), tylko blad; szkolenie sie nie zaczyna.
        if (statementNeeded(response.data.healthStatement) && !String(response.data.healthStatement.text || '').trim()) {
          throw Object.assign(new Error('health statement text missing'), { statementTextMissing: true });
        }
        setMessage(message);
        setPersonName([gotFirstName, gotLastName].filter(Boolean).join(' ') || null);
        setTrainingId(requiredTraining);
        setUserId(uuid);
        setSessionProof(response.data.proof ?? null);
        setStatementError(null);
        setPendingStatement(statementNeeded(response.data.healthStatement)
          ? { text: response.data.healthStatement.text, userId: uuid, code: loginData.code }
          : null);
        setTrainingTelemetryContext({
          trainingId: requiredTraining,
          userUuid: uuid,
          language: loginData.lang || getLanguageCode(),
          loginMode: trybWejscia || (loginData.userId ? 'token' : 'manual'),
        });
        trackTrainingEvent('identified', 'training-login', 'Training user identified', {
          requiredTraining,
          message,
        });
        setShowModal(false);
        setIsLoading(false);
        // Gdy wszystkie szkolenia sa zaliczone, karta powitania (3 s) sama odsyla na `returnUrl`
        // albo na ekran bazowy — patrz efekt `UP_TO_DATE_CARD_MS` nizej.

        return response;
      })
      .catch(error => {
        console.error(t('error_occurred'), error);
        // Backend potrafi ODMOWIC z powodem, ktory czlowiek przy tablecie musi zobaczyc:
        // 403 `self_registration_not_allowed` znaczy „do tej firmy nie zapiszesz sie sam".
        // Zamiana tego na ogolne „wystapil blad" kazala mu probowac w kolko tego samego —
        // komunikat ma nazywac to, co sie stalo, a nie fakt, ze cos sie stalo.
        const odmowa = error?.response?.status === 403 ? error?.response?.data?.message : null;
        setError(error?.statementTextMissing ? t('health_statement_save_failed') : (odmowa || t('error_occurred')));
        setIsLoading(false);
        throw error;
      });
  };

  const performGeneratedNameLogin = (nameLoginData) => {
    const fakeEmail = createFakeEmailForUser(nameLoginData.firstName, nameLoginData.lastName, nameLoginData.company);
    if (!fakeEmail) return;
    setMatchCandidates([]);
    setPendingNameLogin(null);
    performLogin({
      company: nameLoginData.company,
      lang: nameLoginData.lang,
      email: fakeEmail,
    }).catch(() => {
      // `performLogin` ustawia juz `error` (od teraz takze tresc odmowy z backendu) i gasi
      // spinner — tu tylko przerywamy lancuch. Bez tego odrzucona obietnica wychodzila poza
      // komponent jako unhandled rejection; ten sam wzorzec co w `handleQrConfirm`. Stalo sie
      // to osiagalne, gdy bramka samodzielnej rejestracji zaczela oddawac 403.
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    let dataToSend = { company, lang: selectedLanguage ? selectedLanguage.value.substring(0, 2) : getLanguageCode() };

    if (registrationMode) {
      if (!firstName || !lastName || !company) {
        alert(t('fill_all_fields'));
        return;
      }
      const nameLoginData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        company,
        lang: dataToSend.lang,
      };

      setIsLoading(true);
      axios.get(`${apiUrl}user-match-candidates`, {
        params: {
          firstname: nameLoginData.firstName,
          lastname: nameLoginData.lastName,
          company: nameLoginData.company,
        },
      })
        .then(response => {
          const candidates = response.data?.candidates || [];
          if (candidates.length > 0) {
            setPendingNameLogin(nameLoginData);
            setMatchCandidates(candidates);
            setIsLoading(false);
            trackTrainingEvent('account_match_prompt', 'account-match-candidates', 'Account match candidates shown', {
              candidates: candidates.length,
            });
            return;
          }

          performGeneratedNameLogin(nameLoginData);
        })
        .catch(error => {
          console.error(t('error_occurred'), error);
          performGeneratedNameLogin(nameLoginData);
        });
      return;
    } else {
      if (!email || !company) {
        alert(t('fill_all_fields'));
        return;
      }

      // 🔴 NAJPIERW rozpoznanie ISTNIEJACEGO konta, dopiero potem logowanie (werken169,
      // 2026-08-28). `getForUser` z para email+firma trafia na `findOrCreateUserByEmailAndCompany`,
      // ktora przy braku trafienia ZAKLADA nowe konto — literowka w loginie cicho tworzyla konto
      // widmo i szkolenie ladowalo na nim, a kontrola na bramie dalej pokazywala brak. Endpoint
      // `adhoc-identify` rozpoznaje wylacznie konta ISTNIEJACE i nigdy niczego nie zaklada;
      // logujemy sie potem po `userId` (uuid), czyli na to konkretne konto, bez zgadywania firmy.
      setLoginError(null);
      setIsLoading(true);
      axios.post(`${apiUrl}adhoc-identify`, { email: email.trim() })
        .then(response => {
          const { matched, uuid } = response.data || {};
          if (!matched || !uuid) {
            setIsLoading(false);
            setLoginError(t('login_not_found'));
            trackTrainingEvent('login_not_found', 'login-not-found', 'Login did not match any account', {});
            return;
          }
          performLogin({ userId: uuid, lang: dataToSend.lang }).catch(() => {
            // performLogin ustawia juz `error` i gasi spinner.
          });
        })
        .catch(() => {
          setIsLoading(false);
          setLoginError(t('login_not_found'));
          trackTrainingEvent('login_not_found', 'login-not-found', 'Login lookup failed', {});
        });
    }
  };

  const handleMatchCandidateConfirm = (candidate) => {
    const lang = pendingNameLogin?.lang || (selectedLanguage ? selectedLanguage.value.substring(0, 2) : getLanguageCode());
    setMatchCandidates([]);
    setPendingNameLogin(null);
    trackTrainingEvent('account_match_confirmed', 'account-match-confirm', 'Existing account selected', {
      candidateUuid: candidate.uuid,
    });
    performLogin({ userId: candidate.uuid, lang });
  };

  const handleMatchCandidateReject = () => {
    const nameLoginData = pendingNameLogin;
    trackTrainingEvent('account_match_rejected', 'account-match-reject', 'Account match rejected', {
      candidates: matchCandidates.length,
    });
    setMatchCandidates([]);
    setPendingNameLogin(null);
    if (nameLoginData) {
      performGeneratedNameLogin(nameLoginData);
    }
  };

  const handleAdhocSubmit = (event) => {
    event.preventDefault();
    setAdhocError(null);
    const value = adhocIdValue.trim();
    if (!value) {
      setAdhocError(t('adhoc_no_match'));
      return;
    }
    // Send exactly one of { email } | { phone } | { emai } based on selected type,
    // plus the trainingId so the backend can add the identified user to this
    // training's current open attendance list.
    // Bez `trainingId` (wejscie numerem) nie ma listy obecnosci do zapisania.
    const payload = adhocTrainingId
      ? { [adhocIdType]: value, trainingId: adhocTrainingId }
      : { [adhocIdType]: value };

    setIsLoading(true);
    axios.post(`${apiUrl}adhoc-identify`, payload)
      .then(response => {
        setIsLoading(false);
        const { matched, uuid, displayName } = response.data || {};
        if (matched && uuid && !adhocTrainingId) {
          // „Czy to Ty?" bez imienia i nazwiska niczego nie potwierdza — wtedy odmawiamy startu.
          if (!displayName || !String(displayName).trim()) {
            // Konto ISTNIEJE, ale nie ma imienia i nazwiska — to brak danych do uzupelnienia
            // w panelu, nie zly numer. Osobny komunikat kieruje do osoby prowadzacej.
            setAdhocError(t('adhoc_matched_no_name'));
            trackTrainingEvent('adhoc_pending_no_name', 'adhoc-identify', 'Matched account without name', { adhocIdType });
            return;
          }
          setAdhocPending({ uuid, displayName: String(displayName).trim(), adhocIdType });
          trackTrainingEvent('identified', 'adhoc-identify', 'Evidence-number user identified', {
            displayName,
            adhocIdType,
          });
        } else if (matched && uuid) {
          // Replicate post-login entry: set user + training state, close modal.
          const startAdhoc = (healthStatement) => {
            if (statementNeeded(healthStatement) && !String(healthStatement.text || '').trim()) {
              setAdhocError(t('health_statement_save_failed'));
              return;
            }
            setAdhocConfirm(displayName || '');
            setPersonName(displayName ? String(displayName).trim() : null);
            setUserId(uuid);
            setSessionProof(response.data?.proof ?? null);
            setStatementError(null);
            setPendingStatement(statementNeeded(healthStatement)
              ? { text: healthStatement.text, userId: uuid }
              : null);
            setTrainingId(adhocTrainingId);
            setTrainingTelemetryContext({
              trainingId: adhocTrainingId,
              userUuid: uuid,
              loginMode: 'adhoc',
              adhocIdType,
            });
            trackTrainingEvent('identified', 'adhoc-identify', 'Ad-hoc user identified', {
              displayName,
              adhocIdType,
            });
            setShowModal(false);
          };
          const fromIdentify = response.data?.healthStatement;
          if (fromIdentify !== undefined && fromIdentify !== null) {
            startAdhoc(fromIdentify);
          } else {
            // Brak pola w odpowiedzi NIE otwiera bramki zdrowotnej: stan oswiadczenia czytamy z
            // getForUser (jak w pozostalych drogach). Blad odczytu = bramka zamknieta.
            setIsLoading(true);
            axios.post(`${apiUrl}getForUser`, { userId: uuid, lang: getLanguageCode() })
              .then((statementResponse) => {
                setIsLoading(false);
                startAdhoc(statementResponse?.data?.healthStatement);
              })
              .catch(() => {
                setIsLoading(false);
                setAdhocError(t('error_occurred'));
              });
          }
        } else {
          setAdhocError(t('adhoc_no_match'));
        }
      })
      .catch(error => {
        setIsLoading(false);
        // 400 with { matched:false, message } or any failure -> inline retry message
        setAdhocError(t('adhoc_no_match'));
      });
  };

  const handleAdhocPendingConfirm = () => {
    const { uuid, adhocIdType: idType } = adhocPending;
    setAdhocPending(null);
    trackTrainingEvent('adhoc_pending_confirmed', 'adhoc-pending-confirm', 'Evidence-number identity confirmed', { adhocIdType: idType });
    performLogin({ userId: uuid, lang: getLanguageCode() })
      .then(() => {
        // performLogin stempluje `loginMode: 'token'` (wolanie z userId) — w raportach wejscie
        // numerem ma byc odroznialne od linku z kodem.
        setTrainingTelemetryContext({ loginMode: 'evidence-number', adhocIdType: idType });
      })
      .catch(() => {
        // performLogin ustawia juz `error` i gasi spinner.
      });
  };

  const handleAdhocPendingReject = () => {
    // „To nie ja" = numer rozpoznal nie te osobe; bez tego zdarzenia pomylka nie zostawia sladu.
    trackTrainingEvent('adhoc_pending_rejected', 'adhoc-pending-reject', 'Evidence-number identity rejected', {
      adhocIdType: adhocPending?.adhocIdType,
    });
    setAdhocPending(null);
    setAdhocIdValue('');
  };

  /**
   * Odczyt z kamery leci do backendu SUROWY. Kształt kodu (token plakietki po `/q/`, goły hex,
   * numer karty, przekręcony układ AZERTY) rozstrzyga App\Service\GateScanCodeParser — ta sama
   * funkcja, ktorej uzywa brama. Reaktor nie ma wlasnej kopii tych regul i miec jej nie moze:
   * rozjechalyby sie przy pierwszej zmianie formatu.
   *
   * Endpoint zwraca WYLACZNIE tozsamosc do potwierdzenia. Szkolenie startuje dopiero po
   * kliknieciu potwierdzenia (handleQrConfirm) — skan sam z siebie nikogo nie loguje.
   */
  const handleQrDecoded = (rawCode) => {
    setShowQrScanner(false);
    identifyByBadgeCode(rawCode, 'camera');
  };

  /**
   * Rozpoznanie osoby po kodzie plakietki. Dwa wejscia, JEDNA sciezka:
   *  - `camera` — ktos otworzyl reaktor i zeskanowal plakietke wbudowana kamera (tablet w sali);
   *  - `url`    — plakietke zeskanowal telefonem aparatem systemowym, `/q/{token}` przekazal
   *               token w `?badge=` (patrz App\Controller\BadgeRedirectController).
   *
   * 🔴 Dlaczego wejscie z URL-a NIE MOZE spasc do formularza "firma + login" (werken169,
   * 2026-08-28): dopoki token ginal po drodze, reaktor pytal o login i zaliczal szkolenie temu
   * kontu, ktore czlowiek wpisal. Plakietka nalezala do konta 552, wynik wyladowal na 553 —
   * czlowiek przeszkolil CUDZE konto, a kontrola na bramie dalej pokazywala brak szkolenia.
   * Dlatego przy `badgeEntry` nierozpoznany kod konczy sie komunikatem i ponownym skanem,
   * NIGDY recznym logowaniem.
   */
  const identifyByBadgeCode = (rawCode, source) => {
    setQrError(null);
    setIsLoading(true);

    axios.post(`${apiUrl}badge-identify`, { code: rawCode })
      .then(response => {
        setIsLoading(false);
        const { matched, uuid, displayName, companyName } = response.data || {};
        if (!matched || !uuid) {
          setQrError(t('qr_scan_unknown_code'));
          return;
        }
        setQrIdentity({ uuid, displayName, companyName });
        trackTrainingEvent('qr_identified', 'qr-identify', 'Badge scanned and identified', {
          hasCompany: Boolean(companyName),
          source,
        });
      })
      .catch(error => {
        setIsLoading(false);
        // 404 `unknown_code` (nieznana albo odwolana plakietka) i 400 `invalid_code` sa dla
        // czlowieka przy tablecie tym samym zdarzeniem: ten kod tutaj nie dziala. Backend
        // swiadomie nie rozroznia "nie ma takiej plakietki" od "odwolana", zeby nie budowac
        // oracle'a — nie odtwarzamy tego rozroznienia w UI.
        setQrError(t('qr_scan_unknown_code'));
        trackTrainingEvent('qr_identify_failed', 'qr-identify-failed', 'Badge scan not identified', {
          status: error?.response?.status || 0,
          source,
        });
      });
  };

  const handleQrConfirm = () => {
    const identity = qrIdentity;
    if (!identity) return;
    setQrIdentity(null);
    trackTrainingEvent('qr_confirmed', 'qr-confirm', 'Badge identity confirmed', {});
    performLogin({
      userId: identity.uuid,
      lang: selectedLanguage ? selectedLanguage.value.substring(0, 2) : getLanguageCode(),
    }).catch(() => {
      // performLogin ustawia juz `error` i gasi spinner — tu tylko przerywamy lancuch, zeby
      // odrzucona obietnica nie wychodzila poza komponent.
    });
  };

  const handleQrReject = () => {
    setQrIdentity(null);
    setQrError(null);
    trackTrainingEvent('qr_rejected', 'qr-reject', 'Badge identity rejected', { badgeEntry });
    setShowQrScanner(true);
  };

  const openQrScanner = () => {
    setQrError(null);
    setQrIdentity(null);
    setShowQrScanner(true);
    trackTrainingEvent('qr_scan_open', 'qr-scan-open', 'QR scanner opened', {});
  };

  const handleLanguageChange = (selectedOption) => {
    setSelectedLanguage(selectedOption);
    i18next.changeLanguage(selectedOption.value);
    setTrainingTelemetryContext({ language: selectedOption.value });
    trackTrainingEvent('language_change', 'language-select', 'Language changed', {
      language: selectedOption.value,
    });
  };

  /**
   * Firmy, do ktorych backend pozwala dopisac sie SAMODZIELNIE — pole `self_registration_role`
   * z `training/companies` (`supplier` / `courier` / `guest` / `null`). Zaklad przypisuje te role
   * do KONKRETNYCH ID firm w Ustawieniach, bo „Kurier" w kazdym zakladzie nazywa sie inaczej
   * i ma inne ID (Marcin, 2026-09-02).
   *
   * Instalacja, ktora nie przypisala zadnej roli, ma te liste PUSTA — wtedy sciezka „nie mam
   * loginu" znika z ekranu razem z lista firm. Pozostali pracownicy dostaja zaproszenia
   * z systemu i modulu rejestracji, nie ze szkolen.
   *
   * To jest wylacznie warstwa interfejsu. Rozstrzyga bramka w backendzie (`getForUser` oddaje
   * 403 `self_registration_not_allowed`), bo endpoint jest publiczny i starszy build reaktora
   * moze przyslac dowolna firme.
   */
  const selfRegistrationCompanies = useMemo(
    () => companies.filter(item => Boolean(item.self_registration_role)),
    [companies]
  );
  const selfRegistrationAvailable = selfRegistrationCompanies.length > 0;

  /**
   * Tryb samodzielnej rejestracji = INTENCJA uzytkownika (`loginByName`) ∧ MOZLIWOSC (zaklad
   * przypisal role). Wyprowadzamy go, zamiast rozstrzygac przy kliknieciu, bo lista firm dojezdza
   * z API — decyzja podjeta zanim odpowiedz wrocila zapamietywalaby „nie da sie" na zawsze i
   * przycisk „Nie mam karty" po cichu nic by nie robil przy wolnej sieci.
   */
  const registrationMode = loginByName && selfRegistrationAvailable;

  const handleCardDecision = (decision) => {
    setCardDecision(decision);
    setLoginByName(decision === CARD_DECISION.NO_CARD);
    trackTrainingEvent('rfid_card_decision', 'rfid-card-decision', 'RFID card decision selected', {
      decision,
    });
  };

  const resetCardDecision = () => {
    setCardDecision(CARD_DECISION.UNKNOWN);
    setLoginByName(false);
    setCompany('');
    setEmail('');
    setFirstName('');
    setLastName('');
    setRfidUserSearch('');
    setRfidUserSearchResults([]);
    setUsers([]);
  };

  const renderLanguageSelect = () => (
    <Form.Group controlId="formLanguage" className="mb-3">
      <Form.Label>{t('select_language')}</Form.Label>
      <Select
        value={selectedLanguage}
        onChange={handleLanguageChange}
        options={languageOptions}
      />
    </Form.Group>
  );

  const renderCompanySelect = () => {
    // W trybie „nie mam loginu" wybor jest zawezony do rol przelotowych (dostawca/kurier/gosc).
    // Logowanie istniejacego konta widzi pelna liste firm — takze wewnetrzne.
    const options = registrationMode ? selfRegistrationCompanies : companies;

    return (
      <Form.Group controlId="formCompany" className="mb-3">
        <Form.Label>{t('company')}</Form.Label>
        <Form.Control
          as="select"
          value={company}
          onChange={e => setCompany(e.target.value)}
        >
          <option value="">{t('select_company')}</option>
          {options.map(item => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </Form.Control>
      </Form.Group>
    );
  };

  const startNewTraining = (newTrainingId) => {
    setMessage('');
    setError(null);
    setShowModal(false);
    setTrainingId(newTrainingId);
    setTrainingTelemetryContext({ trainingId: newTrainingId });
    trackTrainingEvent('next_training', 'next-training', 'Next training started', {
      trainingId: newTrainingId,
    });
  };

  // Przycisk „Login" zapala sie na czerwono dopiero, gdy formularz ma komplet danych: firma ORAZ
  // login (albo imie i nazwisko w trybie „Nie mam loginu"). Wczesniej jest wygaszony i nieklikalny
  // — zamiast alertu `fill_all_fields` PO kliknieciu (ktory zostaje jako druga linia obrony dla
  // przypadkow spoza tej sciezki, np. autouzupelnienia przegladarki).
  const canSubmitLogin = Boolean(company) && (
    registrationMode
      ? Boolean(firstName.trim()) && Boolean(lastName.trim())
      : Boolean(email.trim())
  );

  const showRfidQuestion = RFID_CARD_FLOW_ENABLED && !isAdhoc && cardDecision === CARD_DECISION.UNKNOWN;
  const showRfidCardList = RFID_CARD_FLOW_ENABLED && !isAdhoc && cardDecision === CARD_DECISION.HAS_CARD;

  return (
    <div className="App container bg-transparent">
      <LoadingSpinner isLoading={isLoading} />

      {/* The gate is deliberately non-dismissible (static backdrop, no keyboard):
          there is nothing behind it until the user identifies. It used to carry a
          close button, and dismissing it dropped straight into the "no training
          left" branch below — telling a NOT logged-in visitor that the training
          was passed. No close button, no false pass. */}
      <Modal show={showModal && !isLoading} backdrop="static" keyboard={false} centered>
        <Modal.Header className="lt-modal-logo-header">
          <img src={liveTrainingLogo} alt="LiveTraining" className="lt-modal-logo" />
        </Modal.Header>
        <Modal.Body>
          {error ? (
            <div className="alert alert-danger">{error}</div>
          ) : qrIdentity ? (
            /* Potwierdzenie PRZED startem szkolenia. Kod QR identyfikuje osobe, ale plakietke
               moze podac ktos inny (albo lezec na stole) — bez tego ekranu tablet cicho
               zalogowalby szkolenie na cudze konto. */
            <div data-track-id="qr-confirm-panel" data-track-pii>
              <p className="mb-2 text-muted">{t('qr_confirm_question')}</p>
              <div className="mb-3">
                <div style={{ fontWeight: 600, fontSize: '1.21rem', color: 'var(--lt-dark, #1e293b)' }}>
                  {qrIdentity.displayName}
                </div>
                {qrIdentity.companyName && (
                  <div className="text-muted">{qrIdentity.companyName}</div>
                )}
              </div>
              <div className="d-grid gap-2">
                <Button variant="danger" onClick={handleQrConfirm} data-track-id="qr-confirm-start">
                  {t('qr_confirm_start')}
                </Button>
                <Button variant="secondary" onClick={handleQrReject} data-track-id="qr-confirm-reject">
                  {t('qr_confirm_not_me')}
                </Button>
              </div>
            </div>
          ) : badgeEntry ? (
            /* 🔴 Wejscie z plakietki (`?badge=`) NIE MA formularza "firma + login" — ani gdy kod
               jest nierozpoznany, ani gdy ktos odrzuci tozsamosc. Tozsamosc niesie plakietka;
               mozliwosc wpisania cudzego loginu w tym miejscu to dokladnie ta droga, ktora
               2026-08-28 na werken169 zaliczyla szkolenie CUDZEMU kontu (552 -> 553). */
            <div data-track-id="badge-entry-panel">
              {isLoading ? (
                <p className="mb-0 text-muted">{t('badge_entry_identifying')}</p>
              ) : (
                <>
                  <div className="alert alert-warning">{t('badge_entry_unknown')}</div>
                  <div className="d-grid">
                    <Button
                      variant="danger"
                      type="button"
                      onClick={openQrScanner}
                      className="d-flex align-items-center justify-content-center gap-2"
                      data-track-id="badge-entry-rescan"
                    >
                      <MdQrCodeScanner size={21} /> {t('badge_entry_rescan')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : matchCandidates.length > 0 ? (
            <>
              <p className="mb-3">{t('account_match_title')}</p>
              <ListGroup className="mb-3">
                {matchCandidates.map(candidate => (
                  <ListGroup.Item
                    action
                    key={candidate.uuid}
                    onClick={() => handleMatchCandidateConfirm(candidate)}
                    data-track-id="account-match-candidate"
                    data-track-pii
                  >
                    <strong>{candidate.firstname} {candidate.lastname}</strong>
                    {candidate.companyName && (
                      <span className="d-block text-muted">{candidate.companyName}</span>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
              <div className="d-grid gap-2">
                <Button variant="secondary" onClick={handleMatchCandidateReject} data-track-id="account-match-new-person">
                  {t('account_match_none')}
                </Button>
              </div>
            </>
          ) : isAdhoc && adhocPending ? (
            <>
              <p>{t('adhoc_confirm_question')}</p>
              <div className="alert alert-success" data-track-id="adhoc-pending-name" data-track-pii>
                <strong>{adhocPending.displayName}</strong>
              </div>
              <div className="d-grid gap-2">
                <Button variant="danger" onClick={handleAdhocPendingConfirm} disabled={isLoading} data-track-id="adhoc-pending-confirm">
                  {t('adhoc_confirm_yes')}
                </Button>
                <Button variant="secondary" onClick={handleAdhocPendingReject} data-track-id="adhoc-pending-reject">
                  {t('adhoc_confirm_no')}
                </Button>
              </div>
            </>
          ) : isAdhoc ? (
            <Form onSubmit={handleAdhocSubmit}>
              {adhocConfirm !== null && (
                <div className="alert alert-success">
                  {t('adhoc_matched', { name: adhocConfirm })}
                </div>
              )}
              <Form.Group controlId="formAdhocType" className="mb-3">
                <Form.Label>{t('adhoc_identifier')}</Form.Label>
                <Form.Control
                  as="select"
                  value={adhocIdType}
                  onChange={e => { setAdhocIdType(e.target.value); setAdhocError(null); }}
                >
                  <option value="email">{t('adhoc_type_email')}</option>
                  <option value="phone">{t('adhoc_type_phone')}</option>
                  <option value="emai">{t('adhoc_type_emai')}</option>
                </Form.Control>
              </Form.Group>
              <Form.Group controlId="formAdhocValue" className="mb-3">
                <Form.Control
                  type="text"
                  placeholder={t('adhoc_enter_value')}
                  value={adhocIdValue}
                  onChange={e => { setAdhocIdValue(e.target.value); setAdhocError(null); }}
                />
              </Form.Group>
              {adhocError && (
                <div className="alert alert-danger">{adhocError}</div>
              )}
              <div className="d-grid mb-3" style={{ marginTop: '21px' }}>
                <Button variant="danger" type="submit" disabled={isLoading}>
                  {t('adhoc_submit')}
                </Button>
              </div>
            </Form>
          ) : showRfidQuestion ? (
            <Form>
              {renderLanguageSelect()}
              <p className="mb-3">{t('rfid_card_question')}</p>
              <div className="d-grid gap-2">
                <Button
                  variant="danger"
                  type="button"
                  onClick={() => handleCardDecision(CARD_DECISION.HAS_CARD)}
                  data-track-id="rfid-has-card"
                >
                  {t('rfid_card_yes')}
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => handleCardDecision(CARD_DECISION.NO_CARD)}
                  data-track-id="rfid-no-card"
                >
                  {t('rfid_card_no')}
                </Button>
              </div>
            </Form>
          ) : showRfidCardList ? (
            <Form>
              {renderLanguageSelect()}
              {renderCompanySelect()}
              <p className="mb-3">{t('rfid_card_instruction')}</p>
              {company && (
                <Form.Group controlId="formRfidUserList" className="mb-3">
                  <Form.Label>{t('rfid_remote_search_label')}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t('rfid_remote_search_placeholder')}
                    value={rfidUserSearch}
                    onChange={e => setRfidUserSearch(e.target.value)}
                    autoComplete="off"
                    data-track-id="rfid-user-search"
                  />
                  <Form.Text className="text-muted d-block mb-2">
                    {t('rfid_remote_search_hint', { count: RFID_SEARCH_MIN_LENGTH })}
                  </Form.Text>
                  {!canShowRfidSearchResults ? (
                    <div className="alert alert-info mb-0">
                      {t('rfid_remote_search_too_short', { count: RFID_SEARCH_MIN_LENGTH })}
                    </div>
                  ) : isRfidUserSearchLoading ? (
                    <div className="alert alert-info mb-0">{t('loading')}</div>
                  ) : rfidUserSearchResults.length > 0 ? (
                    <ListGroup style={{ maxHeight: '233px', overflowY: 'auto' }}>
                      {rfidUserSearchResults.map(user => (
                        <ListGroup.Item
                          action
                          href={`#link-${user.uuid}`}
                          key={user.uuid}
                          onClick={() => handleUserSelect(user)}
                          data-track-id="rfid-user-selected"
                          data-track-pii
                        >
                          {user.name}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <div className="alert alert-warning mb-0">{t('rfid_remote_search_no_results')}</div>
                  )}
                </Form.Group>
              )}
              <div className="d-grid">
                <Button variant="link" type="button" onClick={resetCardDecision}>
                  {t('rfid_back_to_question')}
                </Button>
              </div>
            </Form>
          ) : (
            <>
              <Form onSubmit={handleSubmit}>
                {renderLanguageSelect()}
                {/* Najszybsza droga do szkolenia: plakietka z tym samym kodem QR, ktory czyta
                    ochrona na bramie. Stad przycisk NAD formularzem, nie pod nim. */}
                <div className="d-grid mb-3">
                  <Button
                    variant="danger"
                    type="button"
                    onClick={openQrScanner}
                    className="d-flex align-items-center justify-content-center gap-2"
                    data-track-id="qr-scan-open"
                  >
                    <MdQrCodeScanner size={21} /> {t('qr_scan_button')}
                  </Button>
                </div>
                {qrError && (
                  <div className="alert alert-warning" data-track-id="qr-scan-failed">{qrError}</div>
                )}
                <div className="lt-or-separator mb-3"><span>{t('qr_scan_or')}</span></div>
                {renderCompanySelect()}
                {registrationMode ? (
                  <>
                    {company && !RFID_CARD_FLOW_ENABLED && (
                      <Form.Group controlId="formUserList" className="mb-3">
                        <Form.Label>{t('select_user')}</Form.Label>
                        <ListGroup style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {users.map(user => (
                            <ListGroup.Item
                              action
                              href={`#link${user.uuid}`}
                              key={user.uuid}
                              data-track-id="ostatnio-zalogowany"
                              data-track-pii
                              onClick={() => handleUserSelect(user)}
                            >
                              {user.name} ({user.username})
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </Form.Group>
                    )}
                    <Form.Group controlId="formFirstName">
                      <Form.Label>{t('full_name')}</Form.Label>
                      <Row>
                        <Col>
                          <Form.Control
                            type="text"
                            placeholder={t('enter_first_name')}
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                          />
                        </Col>
                        <Col>
                          <Form.Control
                            type="text"
                            placeholder={t('enter_last_name')}
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                          />
                        </Col>
                      </Row>
                    </Form.Group>
                  </>
                ) : (
                  <Form.Group controlId="formEmail">
                    <Form.Label>{t('login')}</Form.Label>
                    <Row>
                      <Col xs={8}>
                        <Form.Control
                          type="email"
                          placeholder={t('enter_login')}
                          value={email}
                          onChange={e => { setEmail(e.target.value); setLoginError(null); }}
                        />
                      </Col>
                      {selfRegistrationAvailable && (
                        <Col xs={4}>
                          <Button
                            variant="secondary"
                            data-track-id="no-login"
                            onClick={() => {
                              setLoginByName(true);
                              // Firma wybrana do LOGOWANIA moze nie miec przypisanej roli —
                              // zostawiona w polu pokazywalaby pusty select mimo ustawionego
                              // `company` i przepuszczalaby submit do bramki backendu.
                              if (company && !selfRegistrationCompanies.some(item => item.id === company)) {
                                setCompany('');
                                setUsers([]);
                              }
                            }}
                          >
                            {t('no_login')}
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </Form.Group>
                )}
                {loginError && (
                  <div className="alert alert-warning mt-3" data-track-id="login-not-found">{loginError}</div>
                )}
                <div className="d-grid mb-3" style={{ marginTop: '21px' }}>
                  <Button
                    variant={canSubmitLogin ? 'danger' : 'secondary'}
                    type="submit"
                    disabled={isLoading || !canSubmitLogin}
                    data-track-id="login-submit"
                  >
                    {t('login_button')}
                  </Button>
                </div>
                {registrationMode && RFID_CARD_FLOW_ENABLED && (
                  <div className="d-grid">
                    <Button variant="link" type="button" onClick={resetCardDecision}>
                      {t('rfid_back_to_question')}
                    </Button>
                  </div>
                )}
                {registrationMode && !RFID_CARD_FLOW_ENABLED && (
                  <div className="d-grid">
                    <Button variant="link" onClick={() => setLoginByName(false)}>
                      {t('back_to_login')}
                    </Button>
                  </div>
                )}
              </Form>
            </>
          )}
        </Modal.Body>
      </Modal>

      <QrScannerModal
        show={showQrScanner}
        onDecoded={handleQrDecoded}
        onClose={() => setShowQrScanner(false)}
      />

      {!showModal && pendingStatement && (
        <Modal show={true} centered backdrop="static" keyboard={false}>
          <Modal.Header className="lt-modal-logo-header">
            <img src={liveTrainingLogo} alt="LiveTraining" className="lt-modal-logo" />
          </Modal.Header>
          <Modal.Body>
            <HealthStatementCard
              personName={personName}
              text={pendingStatement.text}
              busy={statementBusy}
              error={statementError}
              onConfirm={handleStatementConfirm}
              onCancel={cancelStatement}
            />
          </Modal.Body>
        </Modal>
      )}

      {!showModal && !pendingStatement && (
        (trainingId && (
          <Training
            trainingId={trainingId}
            userId={userId}
            proof={sessionProof}
            isPreview={isPreview}
            startNewTraining={startNewTraining}
            returnUrl={returnUrl}
          />
        )) || (userId && (
          <div className="mt-5">
            <Modal show={true} centered>
              <Modal.Header className="lt-modal-logo-header">
                <img src={liveTrainingLogo} alt="LiveTraining" className="lt-modal-logo" />
              </Modal.Header>
              <Modal.Body className="text-center py-4" data-testid="training-up-to-date-card">
                <h5 className="mb-0" style={{fontWeight: 600, color: 'var(--lt-dark)'}}>
                  {personName ? t('training_up_to_date_greeting', { name: personName }) : (message || t('training_passed'))}
                </h5>
              </Modal.Body>
            </Modal>
          </div>
        ))
      )}
    </div>
  );
};

export default App;
